import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useNotifications,
  useMarkNotificationRead,
  useDismissNotification,
  useMarkAllNotificationsRead,
  type NotificationType,
} from '../api/queries/notifications';
import { ApiError } from '../api/client';
import './NotificationsPage.css';
import { Toggle } from '../components/Toggle';

const TYPE_LABEL: Record<NotificationType, string> = {
  reminder: 'Reminder',
  expiring_item: 'Expiring',
  overdue_invoice: 'Overdue',
  custody_action: 'Custody',
  court_date: 'Court date',
  system: 'System',
};

function fmtRelative(epochMs: number): string {
  const ms = Date.now() - epochMs;
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(epochMs).toLocaleDateString();
}

export function NotificationsPage(): JSX.Element {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const { data, isPending, error } = useNotifications(unreadOnly);
  const markRead = useMarkNotificationRead();
  const dismiss = useDismissNotification();
  const markAll = useMarkAllNotificationsRead();

  if (isPending) return <p className="nf-status">Loading notifications…</p>;
  if (error) {
    const msg = error instanceof ApiError && error.status === 403
      ? "You don't have permission to view notifications."
      : 'Failed to load.';
    return <p className="nf-status nf-error">{msg}</p>;
  }

  const items = data?.notifications ?? [];
  const unreadCount = data?.unread_count ?? 0;

  return (
    <section className="section-shell">
      <header className="section-header">
        <h2>Notifications</h2>
        <div className="nf-header-controls">
          <Toggle
            checked={unreadOnly}
            onChange={setUnreadOnly}
            label="Unread only"
          />
          {unreadCount > 0 && (
            <button
              type="button"
              className="btn-charcoal-secondary nf-mark-all"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
            >
              {markAll.isPending ? 'Marking…' : `Mark all ${unreadCount} read`}
            </button>
          )}
        </div>
      </header>

      {items.length === 0 ? (
        <div className="section-body">
          <div className="nf-empty">
            <p>{unreadOnly ? 'Nothing unread.' : 'No notifications yet.'}</p>
            <p className="nf-empty-meta">
              Notifications fire when expiring items cross their reminder
              window, invoices go overdue, custody actions need attention,
              or admins broadcast a system message.
            </p>
          </div>
        </div>
      ) : (
        <div className="section-body section-body-flush">
        <ul className="nf-list">
          {items.map((n) => {
            const isUnread = n.read_at === null;
            const isDismissed = n.dismissed_at !== null;
            return (
              <li
                key={n.id}
                className={`nf-item ${isUnread ? 'nf-item-unread' : ''} ${isDismissed ? 'nf-item-dismissed' : ''}`}
              >
                <div className="nf-item-main">
                  <div className="nf-item-header">
                    <span className={`nf-pill nf-pill-${n.notification_type}`}>
                      {TYPE_LABEL[n.notification_type]}
                    </span>
                    <span className="nf-item-title">{n.title}</span>
                    <span className="nf-item-time">{fmtRelative(n.created_at)}</span>
                  </div>
                  <p className="nf-item-body">{n.body}</p>
                  {n.link_url && (
                    <Link
                      to={n.link_url}
                      className="nf-item-link"
                      onClick={() => {
                        if (isUnread) markRead.mutate(n.id);
                      }}
                    >
                      View →
                    </Link>
                  )}
                </div>
                <div className="nf-item-actions">
                  {isUnread && (
                    <button
                      type="button"
                      className="nf-link"
                      onClick={() => markRead.mutate(n.id)}
                      disabled={markRead.isPending}
                    >
                      mark read
                    </button>
                  )}
                  {!isDismissed && (
                    <button
                      type="button"
                      className="nf-link"
                      onClick={() => dismiss.mutate(n.id)}
                      disabled={dismiss.isPending}
                    >
                      dismiss
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        </div>
      )}
    </section>
  );
}
